import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export interface TechnicalSheetAllergyCount { text: string; count: number; removable?: boolean; }
export interface TechnicalSheetVariant {
    label: string;
    count: number;
    allergies: TechnicalSheetAllergyCount[];
    /** Allergies ayant motivé le retrait — vide = simple préférence du convive. Le libellé est repris tel
     *  que déclaré, la cuisine devant savoir de QUOI il s'agit pour vérifier son assiette. */
    allergy_reasons?: string[];
}
export interface TechnicalSheetDish {
    name: string;
    category: string;
    total: number;
    base_count: number;
    base_allergies: TechnicalSheetAllergyCount[];
    variants: TechnicalSheetVariant[];
}

interface TechnicalSheetPdfData {
    eventLabel: string;
    eventDate?: string;
    totalResponded: number;
    dishes: TechnicalSheetDish[];
    restrictionsAllergies: string[];
    issuerName?: string;
    logoUrl?: string | null;
    brandColor?: string;
    /** Masque l'encart global et toutes les mentions d'allergies par sous-groupe — case à cocher côté UI pour
     *  une fiche "propre" à laisser en cuisine sans données personnelles. Par défaut affichées. Demande
     *  explicite d'Emeni (2026-08-23) : "on devrait pouvoir cocher une case qui permet de le télécharger sans
     *  faire apparaître les allergies". */
    includeAllergies?: boolean;
}

// jsPDF ne sait pas aller chercher une image derrière une URL http : il attend une data URL. Le logo est
// donc rasterisé via un <canvas> avant d'être injecté, et on retombe sur null (PDF sans logo) plutôt que
// de faire échouer toute la génération si l'image est absente ou bloquée par CORS.
export const loadImageAsDataUrl = (url: string): Promise<{ dataUrl: string; width: number; height: number } | null> =>
    new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext('2d');
                if (!ctx || !img.naturalWidth || !img.naturalHeight) { resolve(null); return; }
                ctx.drawImage(img, 0, 0);
                resolve({ dataUrl: canvas.toDataURL('image/png'), width: img.naturalWidth, height: img.naturalHeight });
            } catch (_) { resolve(null); }
        };
        img.onerror = () => resolve(null);
        img.src = url;
    });

const hexToRgb = (hex?: string): [number, number, number] => {
    const clean = (hex || '#6d28d9').replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16) || 0;
    const g = parseInt(clean.substring(2, 4), 16) || 0;
    const b = parseInt(clean.substring(4, 6), 16) || 0;
    return [r, g, b];
};

const getContrastRgb = (hex?: string): [number, number, number] => {
    const [r, g, b] = hexToRgb(hex);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? [0, 0, 0] : [255, 255, 255];
};

const darken = (rgb: [number, number, number], amount: number): [number, number, number] =>
    rgb.map((c) => Math.max(0, Math.round(c * (1 - amount)))) as [number, number, number];

// Fiche technique cuisine (Phase 5 "Menus & Convives") — même mise en page que les autres récaps du module
// (bandeau dégradé + badge) : total de convives par plat, et un encart séparé pour les allergies/intolérances
// déclarées, pour que la cuisine adapte. Demande explicite d'Emeni (2026-08-22) : "une fiche technique pour
// la cuisine avec un récap du nombre de plats et les intolérances/allergies pour adapter".
export const generateTechnicalSheetPdf = async (data: TechnicalSheetPdfData) => {
    try {
        const { jsPDF } = await import('jspdf');
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

        const primary = hexToRgb(data.brandColor || '#6d28d9');
        const primary2 = darken(primary, 0.28);
        const onPrimary = getContrastRgb(data.brandColor || '#6d28d9');
        const ink: [number, number, number] = [34, 25, 51];
        const inkFaint: [number, number, number] = [138, 131, 160];
        const tint: [number, number, number] = [244, 241, 252];
        const stripe: [number, number, number] = [250, 248, 253];
        const border: [number, number, number] = [228, 223, 242];

        // ── En-tête sans bandeau : logo + nom entreprise, badge coloré ──
        let y = 15;
        let logoDrawW = 0;
        const logo = data.logoUrl ? await loadImageAsDataUrl(data.logoUrl) : null;
        if (logo) {
            const maxW = 24, maxH = 18;
            const ratio = logo.width / logo.height;
            const drawW = ratio > maxW / maxH ? maxW : maxH * ratio;
            const drawH = ratio > maxW / maxH ? maxW / ratio : maxH;
            try {
                doc.addImage(logo.dataUrl, 'PNG', 15, y - 5, drawW, drawH);
                logoDrawW = drawW + 6;
            } catch (e) { console.warn('Could not render logo in PDF:', e); }
        }

        doc.setTextColor(ink[0], ink[1], ink[2]);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text((data.issuerName || 'Récapitulatif').toUpperCase(), 15 + logoDrawW, y + 2);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        const badgeLabel = 'FICHE TECHNIQUE CUISINE';
        const badgeW = doc.getTextWidth(badgeLabel) + 12;
        doc.setFillColor(primary[0], primary[1], primary[2]);
        doc.roundedRect(195 - badgeW, y - 5, badgeW, 9, 4.5, 4.5, 'F');
        doc.setTextColor(onPrimary[0], onPrimary[1], onPrimary[2]);
        doc.text(badgeLabel, 195 - badgeW / 2, y, { align: 'center' });

        y += 18;

        // ── Encadré centré : nom événement + date/heure (couleur de marque, fond blanc) ──
        const dateLabel = data.eventDate ? format(new Date(data.eventDate), 'EEEE d MMMM yyyy', { locale: fr }) : '';
        const timeLabel = data.eventDate ? format(new Date(data.eventDate), 'HH:mm', { locale: fr }) : '';
        const eventBoxH = 22;

        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(primary[0], primary[1], primary[2]);
        doc.setLineWidth(0.6);
        doc.roundedRect(40, y, 130, eventBoxH, 3, 3, 'FD');
        doc.setLineWidth(0.2);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(ink[0], ink[1], ink[2]);
        doc.text(data.eventLabel || 'Privatisation', 105, y + 9.5, { align: 'center' });

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(primary[0], primary[1], primary[2]);
        doc.text(`${dateLabel}${timeLabel ? ' — ' + timeLabel : ''}`, 105, y + 16, { align: 'center' });

        y += eventBoxH + 10;

        const includeAllergies = data.includeAllergies !== false;

        // ── Détail des plats par catégorie, avec sous-groupes par variante et, dans chaque groupe, les
        // allergies/restrictions spécifiques aux convives qui en font partie — ex: "Lieu Noir : 28, dont 3
        // sans sauce (dont 1 allergie cacahuète)". La cuisine doit voir en un coup d'œil à QUEL sous-groupe
        // une allergie précise appartient, pas juste une liste globale mélangée. Demande explicite d'Emeni
        // (2026-08-22).
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10.5);
        doc.setTextColor(ink[0], ink[1], ink[2]);
        doc.text('DÉTAIL PAR PLAT', 15, y);
        y += 8;

        let lastCategory = '';
        data.dishes.forEach((d) => {
            if (y > 270) { doc.addPage(); y = 20; }
            if (d.category !== lastCategory) {
                lastCategory = d.category;
                doc.setFillColor(primary[0], primary[1], primary[2]);
                doc.rect(15, y - 4.5, 180, 7, 'F');
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(8);
                doc.setTextColor(onPrimary[0], onPrimary[1], onPrimary[2]);
                doc.text((d.category || '—').toUpperCase(), 20, y);
                y += 8;
            }

            // Ligne du plat, total en gras + gros. Le nom est limité en largeur pour ne jamais chevaucher la
            // quantité alignée à droite (un nom de variante long comme "Lieu Noir (Sans sauce au beurre
            // blanc, remplacé par...)" pouvait auparavant se superposer au "× N"). Demande explicite d'Emeni
            // (2026-08-23) : "c'est coupé, on ne voit pas la quantité".
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(10.5);
            doc.setTextColor(ink[0], ink[1], ink[2]);
            const nameLines = doc.splitTextToSize(d.name, 150);
            doc.text(nameLines, 20, y);
            doc.setTextColor(primary[0], primary[1], primary[2]);
            doc.text(`× ${d.total}`, 193, y, { align: 'right' });
            y += 5.5 * nameLines.length + 0.5;

            const redAllergy: [number, number, number] = [217, 45, 45];
            const printAllergies = (allergies: TechnicalSheetAllergyCount[], x: number) => {
                if (!includeAllergies) return;
                allergies.forEach((a) => {
                    if (y > 275) { doc.addPage(); y = 20; }
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(8.5);
                    doc.setTextColor(redAllergy[0], redAllergy[1], redAllergy[2]);
                    // Deux consignes de nature différente, qu'il ne faut pas lire de la même façon :
                    //  - retirable  -> la cuisine sait quoi faire, elle compose l'assiette sans l'ingrédient ;
                    //  - non retirable -> personne ne peut rien y faire en cuisine, il faut voir en salle.
                    // Les afficher toutes deux comme "sans X — ALLERGIE" laissait croire que le second cas
                    // était exécutable. Demande d'Emeni (2026-08-23) : la fiche cuisine doit refléter ce qui
                    // est retirable ou non.
                    doc.text(a.removable === false
                        ? `· ALLERGIE ${a.text} — NON RETIRABLE, voir en salle`
                        : `· sans ${a.text} — ALLERGIE`, x, y);
                    // Le nombre de convives concernés était calculé par le serveur mais jamais imprimé : sans
                    // lui, "sans Moutarde" sous "Œufs mayo × 79" se lisait comme s'appliquant aux 79 assiettes
                    // alors qu'il ne concerne souvent qu'un seul convive. Signalé par Emeni (2026-08-23).
                    doc.text(`× ${a.count}`, 193, y, { align: 'right' });
                    y += 4.5;
                });
            };

            // Format simplifié demandé par Emeni, en revenant sur la décomposition précédente : chaque ligne
            // répète le nom du plat suivi du libellé complet de la variante tel quel, sans coupure ni mise en
            // rouge d'une partie du texte — seules les allergies restent en rouge en dessous. Ex: "Lieu Noir
            // x38" puis "Lieu Noir - Sans sauce au beurre blanc, remplacé par un filet d'huile d'olive à
            // l'ail x5" puis "Lieu Noir - Sans poisson x3".
            // Si pas de variantes ET pas d'allergies (d.variants.length === 0 && d.base_allergies.length === 0),
            // on affiche juste une seule ligne — pas de doublon. Demande explicite d'Emeni (2026-08-23).
            const hasDetails = d.variants.length > 0 || d.base_allergies.length > 0;
            if (hasDetails) {
                if (d.base_count > 0) {
                    if (y > 275) { doc.addPage(); y = 20; }
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(8.5);
                    doc.setTextColor(ink[0], ink[1], ink[2]);
                    const baseLines = doc.splitTextToSize(`— ${d.name}`, 145);
                    doc.text(baseLines, 25, y);
                    doc.setTextColor(primary[0], primary[1], primary[2]);
                    doc.text(`× ${d.base_count}`, 193, y + 5 * (baseLines.length - 1), { align: 'right' });
                    y += 5 * baseLines.length;
                    printAllergies(d.base_allergies, 30);
                }

                d.variants.forEach((v) => {
                    if (y > 275) { doc.addPage(); y = 20; }
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(8.5);
                    // Un retrait motivé par une ALLERGIE est écrit en rouge, comme les lignes d'allergie :
                    // c'est une consigne impérative, pas une préférence du convive. Sans cette distinction,
                    // "sans garniture & toppings" se lisait comme un simple caprice. Demande d'Emeni
                    // (2026-08-23) : "il faut le préciser en rouge, et le « sans XXX » aussi".
                    // Le nom du plat reste en noir : seule la CONSIGNE est en rouge. Écrire toute la ligne en
                    // rouge noyait l'information utile — la cuisine doit repérer d'un coup d'œil ce qu'elle
                    // doit changer, pas relire le nom du plat. Demande d'Emeni (2026-08-23).
                    const reasons = (v.allergy_reasons || []).filter(Boolean);
                    const isAllergy = reasons.length > 0 && includeAllergies;
                    doc.setTextColor(ink[0], ink[1], ink[2]);
                    const nameLines2 = doc.splitTextToSize(`— ${d.name}`, 145);
                    doc.text(nameLines2, 25, y);
                    doc.setTextColor(primary[0], primary[1], primary[2]);
                    doc.text(`× ${v.count}`, 193, y + 5 * (nameLines2.length - 1), { align: 'right' });
                    y += 5 * nameLines2.length;
                    // Consigne sur sa propre ligne, en rouge, légèrement indentée.
                    doc.setTextColor(redAllergy[0], redAllergy[1], redAllergy[2]);
                    const consigne = `${v.label}${isAllergy ? `  (ALLERGIE : ${reasons.join(', ')})` : ''}`;
                    const consigneLines = doc.splitTextToSize(consigne, 140);
                    doc.text(consigneLines, 30, y);
                    y += 5 * consigneLines.length;
                    printAllergies(v.allergies, 30);
                });
            }

            y += 3;
        });

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(inkFaint[0], inkFaint[1], inkFaint[2]);
        doc.text(`Document généré le ${format(new Date(), 'dd/MM/yyyy à HH:mm')} — iAmani Connexion`, 105, 290, { align: 'center' });

        doc.save(`Fiche_technique_${(data.eventLabel || 'privatisation').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
    } catch (e) {
        console.error('Technical Sheet PDF Generation Error:', e);
        alert('Erreur lors de la génération du document PDF.');
    }
};
